/**
 * useCurrentUser Hook
 * 
 * Provides current user information and ID utilities
 * This replaces the legacy userId/apiUserId from useAuth
 */

import { useSecureAuthContext } from '../providers/SecureAuthProvider';
import { useGetUserProfileQuery } from '@/api/features/users';
import { 
  getCurrentUserApiId, 
  getCurrentUserAuthId,
  isCurrentUser as isCurrentUserUtil,
  setUserIdMapping,
  clearUserIdMapping
} from '../services/userIdentity';
import { tokenService } from '../services/TokenService';
import { useEffect } from 'react';

interface CurrentUserData {
  // User profile
  user: Record<string, unknown> | null;
  isLoading: boolean;
  
  // ID utilities
  userId: string | null; // Stytch/Auth ID
  apiUserId: string | null; // Backend API ID
  isCurrentUser: (senderId: string | null | undefined) => boolean;
  
  // Token access
  getToken: () => string | null;
}

export function useCurrentUser(): CurrentUserData {
  const { isAuthenticated, user: authUser } = useSecureAuthContext();
  
  // Skip API calls during SSR/prerendering
  const isSSR = typeof window === 'undefined';
  
  // Fetch full user profile when authenticated and not in SSR
  const { data: profileData, isLoading: profileLoading } = useGetUserProfileQuery(undefined, {
    skip: !isAuthenticated || isSSR
  });
  
  // Update user ID mapping when profile data changes
  useEffect(() => {
    // Skip during SSR
    if (isSSR) return;

    if (profileData && typeof profileData === 'object') {
      // Safely extract ids with optional field access
      const profile = profileData as { stytchUserId?: string; id?: string };
      // For new accounts, stytchUserId might be null
      // In that case, use the API ID for both to ensure consistency
      const authId = profile.stytchUserId ?? profile.id;
      const apiId = profile.id;

      if (authId && apiId) {
        setUserIdMapping(authId, apiId);
        // Also update Redux session to ensure consistency
        if (typeof window !== 'undefined') {
          const event = new CustomEvent('userIdMappingUpdated', {
            detail: { authId, apiId }
          });
          window.dispatchEvent(event);
        }
      }
    } else if (authUser?.id) {
      // Fallback: If profile isn't loaded yet but we have auth user
      // Use the auth user ID as both auth and API ID temporarily
      // This ensures messages can be sent while profile loads
      setUserIdMapping(authUser.id, authUser.id);
    } else if (!isAuthenticated) {
      clearUserIdMapping();
    }
  }, [profileData, authUser, isAuthenticated, isSSR]);
  
  // Get current IDs from identity service
  const userId = getCurrentUserAuthId();
  const apiUserId = getCurrentUserApiId();
  
  // Format user profile for compatibility
  const userProfile = profileData ? {
    ...profileData,
    user_id: profileData.stytchUserId || profileData.id,
    apiId: profileData.id,
    name: {
      firstName: profileData.firstName || '',
      lastName: profileData.lastName || '',
    },
    emails: profileData.email ? [{ email: profileData.email, verified: true }] : [],
    profileImageUrl: profileData.profileImageUrl || null,
    avatarColor: profileData.avatarColor || null,
    organizationId: profileData.organizationId || null,
  } : null;
  
  // Return safe defaults during SSR
  if (isSSR) {
    return {
      user: null,
      isLoading: false,
      userId: null,
      apiUserId: null,
      isCurrentUser: () => false,
      getToken: () => null,
    };
  }

  return {
    user: userProfile || authUser,
    isLoading: profileLoading,
    userId,
    apiUserId,
    isCurrentUser: isCurrentUserUtil,
    getToken: () => {
      // Use tokenService directly since it's already imported
      return tokenService.getAccessToken();
    }
  };
}